import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {IScreensaver} from "@/model/Screensaver/screensaverInterfaces";
import responseHandler from "@/plugins/errorHandling";
import {IScreensaverLog} from "@/model/logs/Ilogs";
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from "@/constants/pagination";

interface ScreensaverState {
  screensavers: IScreensaver[];
  screensaver: IScreensaver | null;
  lockersScreensaverIds: number[];
  screensaversLockerIds: number[];
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  errors: IErrors;
  logs: IScreensaverLog[];
}

const state: ScreensaverState = {
  screensavers: [],
  screensaver: null,
  lockersScreensaverIds: [],
  screensaversLockerIds: [],
  currentPage: PAGINATION_FIRST_PAGE,
  lastPage: PAGINATION_DEFAULT_LAST_PAGE,
  total: PAGINATION_DEFAULT_TOTAL,
  perPage: PAGINATION_DEFAULT_PER_PAGE,
  errors: {},
  logs: [],
};

const getters: GetterTree<ScreensaverState, RootState> = {
  screensavers: (state): IScreensaver[] => state.screensavers,
  screensaver: (state): IScreensaver | null => state.screensaver,
  lockersScreensaverIds: (state): number[] => state.lockersScreensaverIds,
  screensaversLockerIds: (state): number[] => state.screensaversLockerIds,
  currentPage: (state): number => state.currentPage,
  lastPage: (state): number => state.lastPage,
  total: (state): number => state.total,
  perPage: (state): number => state.perPage,
  logs: (state): IScreensaverLog[] => state.logs,
  errors: (state) => state.errors,
};

const actions: ActionTree<ScreensaverState, RootState> = {
  fetchScreensavers: ({ commit }): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'screensavers/all'
    }),
    commit,
    'screensaversLoaded'
  ),
  fetchScreensaverList:({commit}, filters: any): any =>
     responseHandler.handle(
        () => axios({
          url: settings.endpoint + 'screensavers/',
          params: {
            ...filters,
          },
        }),
        commit,
        'screensaversPageLoaded'
    ),
  clearScreensavers: ({commit}):any => commit('clearScreensavers'),
  fetchLockersScreensaverIds: ({ commit }, {lockerId}): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/lockers/' + lockerId
      }),
      commit,
      'connectionIdsLoaded'
    );
  },
  fetchScreensaversLockerIds: ({ commit }, screensaverId): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/' + screensaverId + '/lockers',
      }),
      commit,
      'connectionIdsLoaded'
    );
  },


  fetchLockerScreensaversLogs: ({commit}: any, {filters, lockerId}: any): any => responseHandler.handle(
    () => axios({
      url: `${settings.endpoint}screensavers/lockers/${lockerId}/logs`,
      params: {
        ...filters,
      },
      method: 'GET'
    }),
    commit,
    'screensaverLogsLoaded'
  ),

  fetchScreensaverLogs: ({commit}: any, {filters, screensaverId}: any): any => responseHandler.handle(
    () => axios({
      url: `${settings.endpoint}screensavers/${screensaverId}/logs`,
      params: {
        ...filters,
      },
      method: 'GET'
    }),
    commit,
    'screensaverLogsLoaded'
  ),

  fetchLogs: ({commit}: any, {filters}: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'screensavers/logs',
      params: {
        ...filters,
      },
      method: 'GET'
    }),
    commit,
    'screensaverLogsLoaded'
  ),

  createNewScreensaver: ({ commit }, type): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/new',
        method: 'POST',
        data: {
          type
        }
      }),
      commit,
      'newScreensaverCreated'
    );
  },
  fetchScreensaver: ({ commit }, screensaverId: number): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/' + screensaverId
      }),
      commit,
      'screensaverDetailLoaded'
    );
  },
  fetchScreensaverTemplate: ({ commit }, type: string): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/templates/' + type
      }),
      commit,
      'screensaverDetailLoaded'
    );
  },
  createScreensaver: ({ commit }, data: any): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/',
        method: 'POST',
        data,
      }),
      commit,
      'screensaverDetailLoaded'
    );
  },
  updateScreensaver: ({ commit }, {screensaverId, data}: {screensaverId: number, data: any}): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/' + screensaverId,
        method: 'PATCH',
        data,
      }),
      commit,
      'screensaverDetailLoaded'
    );
  },
  connectLockerScreensaver: ({ commit }, {lockerId, screensaverId, connected}): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/' + screensaverId + '/lockers/' + lockerId,
        method: connected ? 'PUT' : 'DELETE',
      }),
      commit,
      'connectionIdsLoaded'
    )
  },
  setScreensaverActive: ({ commit }, {screensaverId, active}): any => {
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'screensavers/' + screensaverId + '/active',
        method: 'PUT',
        data: {
          active: active,
        }
      }),
      commit,
      'screensaverActiveUpdated'

    )
  },
  clearLogs:  ({commit}): any => {
    commit('clearLogs');
  },
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  }
};

const mutations: MutationTree<ScreensaverState> = {
  screensaversLoaded(state, payload: IScreensaver[]) {
    state.screensavers = payload;
  },
  screensaversPageLoaded(state, payload: any) {
    if (!payload) {
      state.screensavers = [];
      return;
    }
    state.screensavers = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.currentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.lastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.total = payload.total || PAGINATION_DEFAULT_TOTAL;
    state.perPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
  },
  connectionIdsLoaded(state, payload: {screensavers: number[], lockers: number[]}) {
    state.lockersScreensaverIds = payload.screensavers;
    state.screensaversLockerIds = payload.lockers;
  },
  screensaverActiveUpdated(state, payload: IScreensaver) {
    const screensaver: IScreensaver|undefined = state.screensavers.find((each: IScreensaver) => each.id == payload.id);
    if (screensaver) {
      Object.assign(screensaver, payload as any);
    }
  },
  screensaverLogsLoaded(state, payload: IScreensaverLog[]) {
    state.logs = state.logs.concat(payload);
  },
  newScreensaverCreated(state) {
    // we will redirect to the old edit page after creation (by now)
  },
  screensaverDetailLoaded(state, payload: IScreensaver) {
    state.screensaver = payload;
  },
  clearLogs(state) {
    state.logs = [];
  },
  clearScreensavers(state) {
    state.screensavers = [];
    state.currentPage = PAGINATION_FIRST_PAGE;
    state.lastPage = PAGINATION_DEFAULT_LAST_PAGE;
    state.total = PAGINATION_DEFAULT_TOTAL;
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
};

const namespaced: boolean = true;

export const screensaverModule: Module<ScreensaverState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
